import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  XIcon,
  DownloadIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  Loader2Icon,
  MailIcon,
  CheckCircle2Icon,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TR_MONTHS = [
  'Ocak','Şubat','Mart','Nisan','Mayıs','Haziran',
  'Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık',
];

/**
 * SoloExportModal — produces a downloadable monthly / yearly export of the
 * user's solo_expenses, ready to forward to muhasebeci.
 *
 * Options:
 *   • Year (current year ± 5)
 *   • Whole year or single month
 *   • Type filter: Şirket / Kişisel / Tümü
 *   • Format: CSV (UTF-8 + BOM, ; separator → opens in Türkçe Excel) or PDF
 *
 * Files are streamed from `GET /api/solo/expenses/export` and saved client-
 * side via a blob anchor. Tenant scoping happens server-side; we do not
 * leak filters into the URL beyond the date / type parameters.
 */
const SoloExportModal = ({ onClose }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1..12, 0 = full year
  const [type, setType] = useState('company');
  const [format, setFormat] = useState('csv');
  const [downloading, setDownloading] = useState(false);
  // Mail-to-accountant panel state — collapsed by default; the user expands
  // it via the "Mail ile Gönder" footer button.
  const [showMailPanel, setShowMailPanel] = useState(false);
  const [accountantEmail, setAccountantEmail] = useState('');
  const [ccSelf, setCcSelf] = useState(true);
  const [saveDefault, setSaveDefault] = useState(true);
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);

  // Auto-fill saved accountant address (from /auth/me) so the user doesn't
  // re-type it every month. We fetch lazily on mount.
  useEffect(() => {
    const fetchSaved = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const saved = r.data?.user?.accountant_email || r.data?.accountant_email;
        if (saved) setAccountantEmail(saved);
      } catch (_e) {
        /* silent — empty field is fine */
      }
    };
    fetchSaved();
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        year: String(year),
        type,
        format,
      });
      if (month !== 0) params.set('month', String(month));
      const url = `${BACKEND_URL}/api/solo/expenses/export?${params.toString()}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || 'Dışa aktarım başarısız');
      }
      const blob = await res.blob();
      const dispo = res.headers.get('Content-Disposition') || '';
      const match = dispo.match(/filename="([^"]+)"/);
      const filename =
        match?.[1] ||
        `solo-${type}-${year}${month ? `-${String(month).padStart(2, '0')}` : ''}.${format}`;
      const link = document.createElement('a');
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${filename} indirildi`);
      onClose?.();
    } catch (e) {
      toast.error(e.message || 'Dışa aktarım başarısız');
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = async () => {
    if (!accountantEmail.trim()) {
      toast.error('Muhasebeci e-posta adresi gerekli');
      return;
    }
    setSending(true);
    setSentInfo(null);
    try {
      const token = localStorage.getItem('token');
      const body = {
        year,
        type,
        format,
        recipient_email: accountantEmail.trim(),
        cc_self: ccSelf,
        save_accountant: saveDefault,
      };
      if (month !== 0) body.month = month;
      const r = await axios.post(
        `${BACKEND_URL}/api/solo/expenses/send-report`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSentInfo(r.data);
      if (r.data?.mocked) {
        toast.success(
          `Mail gönderimi simüle edildi (SendGrid aktif değil). Hedef: ${r.data.recipient}`,
        );
      } else if (r.data?.sent) {
        toast.success(`Rapor ${r.data.recipient} adresine gönderildi`);
      } else {
        toast.error('Mail gönderilemedi');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mail gönderimi başarısız');
    } finally {
      setSending(false);
    }
  };

  const yearOptions = [];
  for (let y = now.getFullYear() - 5; y <= now.getFullYear() + 1; y++) {
    yearOptions.push(y);
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="solo-export-modal"
    >
      <Card
        className="w-full max-w-md p-0 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <DownloadIcon className="w-5 h-5 text-purple-600" /> Aylık Rapor İndir
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Vergi/muhasebe için harcama dökümünü tek tıkla CSV ya da PDF olarak indirin.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="solo-export-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Period */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Yıl</label>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
                data-testid="solo-export-year"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Ay</label>
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
                data-testid="solo-export-month"
              >
                <option value={0}>Tüm yıl</option>
                {TR_MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Type */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">İçerik</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 'company',  label: 'Şirket', hint: 'Vergi düşürülebilir' },
                { v: 'personal', label: 'Kişisel', hint: 'Kişisel ödemeler' },
                { v: 'all',      label: 'Tümü',    hint: 'Tam liste' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setType(opt.v)}
                  className={`p-2 rounded-lg border-2 text-left transition-colors ${
                    type === opt.v
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`solo-export-type-${opt.v}`}
                >
                  <div className="font-medium text-sm text-gray-900">{opt.label}</div>
                  <div className="text-[10px] text-gray-500 leading-tight">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Format */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Dosya tipi</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormat('csv')}
                className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-colors ${
                  format === 'csv'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                data-testid="solo-export-format-csv"
              >
                <FileSpreadsheetIcon className="w-5 h-5 text-emerald-600" />
                <div className="text-left">
                  <div className="font-medium text-sm text-gray-900">CSV</div>
                  <div className="text-[10px] text-gray-500">Excel uyumlu</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setFormat('pdf')}
                className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-colors ${
                  format === 'pdf'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                data-testid="solo-export-format-pdf"
              >
                <FileTextIcon className="w-5 h-5 text-rose-600" />
                <div className="text-left">
                  <div className="font-medium text-sm text-gray-900">PDF</div>
                  <div className="text-[10px] text-gray-500">Yazdırılabilir</div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mail-to-accountant panel — toggles open below the form */}
        {showMailPanel && (
          <div className="px-5 py-4 border-t bg-purple-50/30 space-y-3" data-testid="solo-export-mail-panel">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                <MailIcon className="w-3.5 h-3.5 text-purple-600" /> Muhasebeci e-posta adresi
              </label>
              <Input
                type="email"
                value={accountantEmail}
                onChange={(e) => setAccountantEmail(e.target.value)}
                placeholder="muhasebeci@firma.com"
                data-testid="solo-export-mail-recipient"
                autoComplete="off"
              />
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-700">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ccSelf}
                  onChange={(e) => setCcSelf(e.target.checked)}
                  data-testid="solo-export-mail-cc-self"
                />
                CC kendime gönder
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveDefault}
                  onChange={(e) => setSaveDefault(e.target.checked)}
                  data-testid="solo-export-mail-save-default"
                />
                Bu adresi varsayılan yap
              </label>
            </div>
            {sentInfo && (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5 flex items-start gap-1.5" data-testid="solo-export-mail-status">
                <CheckCircle2Icon className="w-3.5 h-3.5 mt-px flex-shrink-0" />
                <span>
                  {sentInfo.mocked
                    ? `Simüle edildi → ${sentInfo.recipient}`
                    : sentInfo.sent
                    ? `Gönderildi → ${sentInfo.recipient}${sentInfo.cc?.length ? ` (CC: ${sentInfo.cc.join(', ')})` : ''}`
                    : 'Gönderim başarısız'}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="p-5 border-t bg-gray-50 flex justify-end gap-2 flex-wrap">
          <Button type="button" variant="outline" onClick={onClose} disabled={downloading || sending}>
            İptal
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowMailPanel((v) => !v)}
            disabled={downloading || sending}
            data-testid="solo-export-toggle-mail"
          >
            <MailIcon className="w-4 h-4 mr-2" />
            {showMailPanel ? 'Mail panelini kapat' : 'Mail ile Gönder'}
          </Button>
          {showMailPanel ? (
            <Button
              type="button"
              onClick={handleSend}
              disabled={sending || !accountantEmail.trim()}
              data-testid="solo-export-send"
              className="btn-primary"
            >
              {sending ? (
                <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <MailIcon className="w-4 h-4 mr-2" />
              )}
              Gönder
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              data-testid="solo-export-download"
              className="btn-primary"
            >
              {downloading ? (
                <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <DownloadIcon className="w-4 h-4 mr-2" />
              )}
              İndir
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SoloExportModal;
